import React, {useEffect, useRef, useState} from "react";
import {Button} from "primereact/button";
import {Toolbar} from "primereact/toolbar";
import {Dialog} from "primereact/dialog";
import {Toast} from "primereact/toast";
import {Dashboard} from "api/model/dashboard/Dashboard";
import {DashboardReportsApi} from "api/DashboardReportsApi";
import {DashboardReportEditor} from "./DashboardReportEditor";
import {addToDate, truncateDate} from "utils/time-utils";
import {TabPanel, TabView} from "primereact/tabview";
import GeneratedReportTable from "../../dashboard/generated-report-table/GeneratedReportTable";
import {DashboardComponentGroup} from "api/model/dashboard/DashboardComponentGroup";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import {useGetDashboard, useUpdateDashboard} from "api/api-hooks/dashboards-hooks";
import {DashboardComponentMappingWrapper} from "../dashboard/components/dashboard-utils";
import LayoutType = DashboardComponentGroup.LayoutType;
import Justify = DashboardComponentGroup.Justify;
import Align = DashboardComponentGroup.Align;


export default function DashboardReportPage() {
    const [showConfigDialog, setShowConfigDialog] = useState(false);
    const [printModeToggled, setPrintModeToggled] = useState(false);
    const [refTime, setRefTime] = useState<Date | undefined>(undefined);
    const [activeIndex, setActiveIndex] = useState(0);
    const toast = useRef<Toast>(null);

    // dashboardId from query params
    const dashboardId = new URLSearchParams(window.location.search).get("dashboardId")!!
    const id = parseInt(dashboardId)

    const {data, isSuccess} = useGetDashboard(id)

    useEffect(() => {
        if (data && isSuccess) {
            const truncateTo = data?.reportOptions?.timingOptions?.referenceTimeTruncateTo ?? "HOURS"
            const offset = data?.reportOptions?.timingOptions?.referenceTimeOffset ?? 0
            const offsetUnit = data?.reportOptions?.timingOptions?.referenceTimeOffsetUnit ?? "HOURS"
            let refTime = truncateDate(new Date(), truncateTo)
            refTime = addToDate(refTime, offset, offsetUnit)
            setRefTime(refTime)
        }
    }, [data, isSuccess])

    const updateQuery = useUpdateDashboard({
        onSuccess: () => {
            toast.current?.show({severity: 'success', summary: 'Success', detail: 'Dashboard updated', life: 3000});
            setShowConfigDialog(false)
        },
        onFailure: (error: Error) => {
            toast.current?.show({severity: 'error', summary: 'Error', detail: error.message, life: 3000})
        },
    });

    const openPreviewTab = () => {
        if (!refTime) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Reference time not initiated',
                life: 3000
            });
            return
        }
        window.open(DashboardReportsApi.getPreviewUrl(id, refTime, data?.reportOptions?.meta?.jsBuild), '_blank')?.focus()
    }

    const genAndDlPdf = () => {
        if (!refTime) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Reference time not initiated',
                life: 3000
            });
            return
        }

        window.open(DashboardReportsApi.getGenerationAndDownloadUrl({
            dashboardId: id,
            referenceTime: refTime.toISOString()
        }), '_blank')?.focus()
    }

    const updateDashboard = (dashboard: Dashboard) => {
        updateQuery.mutate(dashboard);
    }

    console.log("Render DashboardReportPage")


    const leftContents = (
        <React.Fragment>
            <Button label="Edit" icon="pi pi-pencil" className="mr-2" onClick={() => setShowConfigDialog(true)}/>
            <Button label="Toggle Print Mode" icon="pi pi-print" className="mr-2 p-button-warning"
                    onClick={() => setPrintModeToggled(!printModeToggled)}/>
        </React.Fragment>
    );

    const rightContents = (
        <React.Fragment>
            <CalendarWrapper placeholder="Reference time" dateFormat="yy-mm-dd" className="mr-2" value={refTime}
                             onChange={setRefTime}/>
            <Button label="Generate & Download PDF" icon="pi pi-file-pdf" className="p-button-help p-mr-2"
                    onClick={genAndDlPdf}/>
            <Button label="Preview" icon="pi pi-file" className="p-button-help" onClick={openPreviewTab}/>
        </React.Fragment>
    );

    const renderBody = () => {
        return (
            <React.Fragment>
                <h1>Dashboard {data?.title}</h1>

                {data?.dashboardTabsJs?.map((c, tabIdx) => {
                    return <div key={tabIdx} style={{border: "dotted 2px", padding: "0.5rem", marginTop: "2rem"}}>
                        <h3>Tab {tabIdx}: {c.title}</h3>

                        {c.componentGroups?.map((g, groupIdx) => {
                            return <ComponentGroupWrapper key={groupIdx} i={groupIdx} group={g}>
                                <React.Fragment>
                                    {g.components?.map((component, componentIdx) => {
                                        return <div key={componentIdx} style={{
                                            border: "solid 1px",
                                            marginTop: "2.5rem",
                                            background: "white"
                                        }}>
                                            <h5>Component {componentIdx}: {component.title} [{component.type}]</h5>

                                            <DashboardComponentMappingWrapper id={`${component.type}-${tabIdx}-${groupIdx}-${componentIdx}`}
                                                                              component={component.type}
                                                                              config={component.config}
                                                                              referenceTime={refTime!!.toISOString()}
                                            />
                                        </div>
                                    })}
                                </React.Fragment>
                            </ComponentGroupWrapper>
                        })}
                    </div>
                })}
            </React.Fragment>
        )
    }


    return (
        <div className={printModeToggled ? "print-mode-enabled" : ""}>
            <Toast ref={toast}/>
            <Toolbar left={leftContents} right={rightContents}/>

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                <TabPanel header="Preview">
                    {!data || !refTime && "Loading dashboard config..."}
                    {data && refTime && renderBody()}
                </TabPanel>
                <TabPanel header="Controls">
                    <GeneratedReportTable ids={[id]} superUserComponent={true} loadingConfig={{
                        truncateStartUnit: "DAYS",
                        offsetUnit: "DAYS",
                        offset: 1,
                        periodUnit: "DAYS",
                        period: -30
                    }}/>
                </TabPanel>
            </TabView>


            <Dialog header="Edit Dashboard" visible={showConfigDialog} contentStyle={{overflow: "initial"}}
                    style={{width: 'min(100%, 1000px)', height: "70vh"}}
                    onHide={() => {
                        setShowConfigDialog(false)
                    }}>
                <DashboardReportEditor dashboard={data!!}
                                       onChange={updateDashboard}
                                       cancel={() => setShowConfigDialog(false)}/>
            </Dialog>
        </div>
    )
}

const ComponentGroupWrapper = (props: { group: DashboardComponentGroup, children: JSX.Element | JSX.Element[], i: number }) => {

    return <div style={{border: "dashed 2px", padding: "1rem", marginTop: "1rem", background: "lightpink"}}>
        <h4>Component Group {props.i}</h4>

        <ComponentGroupLayoutWrapper group={props.group}>
            {props.children}
        </ComponentGroupLayoutWrapper>
    </div>
}

export const ComponentGroupLayoutWrapper = (props: { group: DashboardComponentGroup, children: JSX.Element | JSX.Element[] }) => {
    const layout = props.group.layout
    const type = layout.type ?? LayoutType.VERTICAL

    let className = ''
    if (type === LayoutType.HORIZONTAL_FLEX) {
        className = `flex justify-content-${(layout.justify ?? Justify.BETWEEN).toLowerCase()} align-items-${(layout.align ?? Align.CENTER).toLowerCase()}  gap-${layout.gap ?? 2}`
    }

    return <div className={className}>
        {props.children}
    </div>
}